import { supabase } from './supabase';

// Email service for sending assessment invitations and reports
export interface EmailOptions {
  to: string;
  subject: string;
  html: string;
  from?: string;
}

// Send email using Supabase Edge Function
export const sendEmail = async (options: EmailOptions): Promise<{ success: boolean; error?: any }> => {
  try {
    // Get the current user's JWT token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.error('No active session found');
      return { success: false, error: 'Not authenticated' };
    }
    
    // Call our Supabase Edge Function to send email
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        to: options.to,
        subject: options.subject,
        html: options.html,
        from: options.from || 'Mentra <noreply@mentra.com>',
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to send email:', errorData);
      return { success: false, error: errorData.error || 'Failed to send email' };
    }
    
    const result = await response.json();
    return { success: true, ...result };
  } catch (error) {
    console.error('Error sending email:', error);
    return { success: false, error };
  }
};

// Generate assessment invitation email
export const generateAssessmentInvitationEmail = (
  employeeName: string,
  companyName: string,
  assessmentLink: string
): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Your Wellness Assessment Invitation</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #334155;
          margin: 0;
          padding: 0;
          background-color: #f8fafc;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .header {
          text-align: center;
          padding: 20px 0;
          background: linear-gradient(135deg, #5d7360 0%, #10b981 100%);
          border-radius: 8px 8px 0 0;
          margin: -20px -20px 20px;
        }
        .header h1 {
          color: white;
          margin: 0;
          font-size: 24px;
        }
        .content {
          padding: 20px;
        }
        .button {
          display: inline-block;
          background: linear-gradient(135deg, #5d7360 0%, #10b981 100%);
          color: white;
          text-decoration: none;
          padding: 12px 24px;
          border-radius: 4px;
          font-weight: bold;
          margin: 20px 0;
        }
        .footer {
          text-align: center;
          margin-top: 30px;
          font-size: 12px;
          color: #64748b;
        }
        .info-box {
          background-color: #f0f9ff;
          border-left: 4px solid #0ea5e9;
          padding: 15px;
          margin: 20px 0;
          border-radius: 0 4px 4px 0;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Mentra Wellness Assessment</h1>
        </div>
        <div class="content">
          <p>Hello ${employeeName},</p>
          
          <p>You've been invited by ${companyName} to complete a wellness assessment as part of our commitment to supporting your mental health and wellbeing.</p>
          
          <p>This assessment is completely confidential and will help us better understand how to support you and your colleagues.</p>
          
          <div class="info-box">
            <p><strong>About the assessment:</strong></p>
            <ul>
              <li>Takes only 5-10 minutes to complete</li>
              <li>Your responses are private and secure</li>
              <li>Choose from voice, text, or quick survey formats</li>
              <li>Receive personalized insights and recommendations</li>
            </ul>
          </div>
          
          <p style="text-align: center;">
            <a href="${assessmentLink}" class="button">Start Your Assessment</a>
          </p>
          
          <p>This link is unique to you and will expire in 7 days. If you have any questions or concerns, please contact your HR representative.</p>
          
          <p>Thank you for your participation!</p>
          
          <p>Best regards,<br>The Mentra Team</p>
        </div>
        <div class="footer">
          <p>This email was sent by Mentra on behalf of ${companyName}.</p>
          <p>© 2025 Mentra. All rights reserved.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};

// Generate assessment report email for HR
export const generateAssessmentReportEmail = (
  hrName: string,
  employeeName: string,
  assessmentDate: string,
  assessmentType: string,
  overallScore: number,
  recommendations: string[],
  reportUrl: string
): string => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Employee Assessment Report</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #334155;
          margin: 0;
          padding: 0;
          background-color: #f8fafc;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        .header {
          text-align: center;
          padding: 20px 0;
          background: linear-gradient(135deg, #5d7360 0%, #10b981 100%);
          border-radius: 8px 8px 0 0;
          margin: -20px -20px 20px;
        }
        .header h1 {
          color: white;
          margin: 0;
          font-size: 24px;
        }
        .content {
          padding: 20px;
        }
        .button {
          display: inline-block;
          background: linear-gradient(135deg, #5d7360 0%, #10b981 100%);
          color: white;
          text-decoration: none;
          padding: 12px 24px;
          border-radius: 4px;
          font-weight: bold;
          margin: 20px 0;
        }
        .footer {
          text-align: center;
          margin-top: 30px;
          font-size: 12px;
          color: #64748b;
        }
        .score-box {
          background-color: #f0fdf4;
          border-radius: 8px;
          padding: 15px;
          margin: 20px 0;
          text-align: center;
        }
        .score {
          font-size: 36px;
          font-weight: bold;
          color: #10b981;
        }
        .recommendations {
          background-color: #f8fafc;
          border-radius: 8px;
          padding: 15px;
          margin: 20px 0;
        }
        .confidential {
          background-color: #fff7ed;
          border-left: 4px solid #f59e0b;
          padding: 10px 15px;
          margin: 20px 0;
          font-size: 12px;
          color: #92400e;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Employee Wellness Report</h1>
        </div>
        <div class="content">
          <p>Hello ${hrName},</p>
          
          <p>A new wellness assessment has been completed by <strong>${employeeName}</strong> on ${assessmentDate}.</p>
          
          <div class="score-box">
            <p>Overall Wellness Score</p>
            <div class="score">${Math.round(overallScore * 100)}%</div>
            <p>Assessment Type: ${assessmentType}</p>
          </div>
          
          <div class="recommendations">
            <p><strong>Key Recommendations:</strong></p>
            <ul>
              ${recommendations.map(rec => `<li>${rec}</li>`).join('')}
            </ul>
          </div>
          
          <p style="text-align: center;">
            <a href="${reportUrl}" class="button">View Full Report</a>
          </p>
          
          <div class="confidential">
            <p><strong>Confidential Information:</strong> This report contains sensitive employee wellness data and should be handled according to your company's privacy policies and HIPAA compliance requirements.</p>
          </div>
          
          <p>Thank you for using Mentra to support your team's wellbeing.</p>
          
          <p>Best regards,<br>The Mentra Team</p>
        </div>
        <div class="footer">
          <p>© 2025 Mentra. All rights reserved.</p>
          <p>This email and its contents are confidential and intended solely for the addressee.</p>
        </div>
      </div>
    </body>
    </html>
  `;
};